import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { apiRequest } from "../../core/common/api";
import ToggleSwitch from "../../shared/ToggleSwitch";

type VideoLink = {
  title: string;
  url: string;
};

type VideoListKey = "seminars" | "achievements";

const VIDEO_LISTS: Array<{ key: VideoListKey; label: string; hint: string }> = [
  { key: "seminars", label: "Company Seminar Videos", hint: "Seminar recordings shown in the app" },
  { key: "achievements", label: "Achievement Program Videos", hint: "Achievement and award program videos shown in the app" },
];

const mapVideoLinks = (list: any): VideoLink[] =>
  Array.isArray(list)
    ? list.map((item: any) => ({ title: item?.title || "", url: item?.url || "" }))
    : [];

type SettingsForm = {
  general: {
    appName: string;
    tagline: string;
    supportEmail: string;
    supportPhone: string;
    whatsappNumber: string;
    address: string;
  };
  features: {
    maintenanceMode: boolean;
    allowNewRegistrations: boolean;
    allowReviews: boolean;
  };
  orders: {
    defaultShippingCharge: string;
    returnWindowDays: string;
    allowCod: boolean;
    gateways: Record<string, boolean>;
  };
  mlm: {
    withdrawAdminPercent: string;
    tdsPercent: string;
    minWithdrawAmount: string;
    profitWithdrawStartHour: string;
    profitWithdrawEndHour: string;
  };
  videos: Record<VideoListKey, VideoLink[]>;
};

const emptyForm = (): SettingsForm => ({
  general: {
    appName: "Riseway",
    tagline: "",
    supportEmail: "",
    supportPhone: "",
    whatsappNumber: "",
    address: "",
  },
  features: {
    maintenanceMode: false,
    allowNewRegistrations: true,
    allowReviews: true,
  },
  orders: {
    defaultShippingCharge: "0",
    returnWindowDays: "7",
    allowCod: true,
    gateways: {},
  },
  mlm: {
    withdrawAdminPercent: "10",
    tdsPercent: "5",
    minWithdrawAmount: "100",
    profitWithdrawStartHour: "10",
    profitWithdrawEndHour: "18",
  },
  videos: {
    seminars: [],
    achievements: [],
  },
});

const mapSettingsToForm = (settings: any): SettingsForm => ({
  general: {
    appName: settings?.general?.appName || "Riseway",
    tagline: settings?.general?.tagline || "",
    supportEmail: settings?.general?.supportEmail || "",
    supportPhone: settings?.general?.supportPhone || "",
    whatsappNumber: settings?.general?.whatsappNumber || "",
    address: settings?.general?.address || "",
  },
  features: {
    maintenanceMode: settings?.features?.maintenanceMode === true,
    allowNewRegistrations: settings?.features?.allowNewRegistrations !== false,
    allowReviews: settings?.features?.allowReviews !== false,
  },
  orders: {
    defaultShippingCharge: String(settings?.orders?.defaultShippingCharge ?? 0),
    returnWindowDays: String(settings?.orders?.returnWindowDays ?? 7),
    allowCod: settings?.orders?.allowCod !== false,
    gateways: { ...(settings?.orders?.gateways || {}) },
  },
  mlm: {
    withdrawAdminPercent: String(settings?.mlm?.withdrawAdminPercent ?? 10),
    tdsPercent: String(settings?.mlm?.tdsPercent ?? 5),
    minWithdrawAmount: String(settings?.mlm?.minWithdrawAmount ?? 100),
    profitWithdrawStartHour: String(settings?.mlm?.profitWithdrawStartHour ?? 10),
    profitWithdrawEndHour: String(settings?.mlm?.profitWithdrawEndHour ?? 18),
  },
  videos: {
    seminars: mapVideoLinks(settings?.videos?.seminars),
    achievements: mapVideoLinks(settings?.videos?.achievements),
  },
});

const isValidVideoUrl = (value: string) => {
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

type PaymentGateway = { name: string; label: string };

const Settings = () => {
  const [form, setForm] = useState<SettingsForm>(emptyForm);
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await apiRequest("/settings", "GET");
      setForm(mapSettingsToForm(result.settings));
      setGateways(Array.isArray(result.settings?.availableGateways) ? result.settings.availableGateways : []);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load settings", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSettings();
  }, []);

  const updateVideos = (key: VideoListKey, videos: VideoLink[]) => {
    setForm((current) => ({ ...current, videos: { ...current.videos, [key]: videos } }));
  };

  const addVideo = (key: VideoListKey) => {
    updateVideos(key, [...form.videos[key], { title: "", url: "" }]);
  };

  const changeVideo = (key: VideoListKey, index: number, field: keyof VideoLink, value: string) => {
    updateVideos(
      key,
      form.videos[key].map((video, position) => (position === index ? { ...video, [field]: value } : video)),
    );
  };

  const removeVideo = (key: VideoListKey, index: number) => {
    updateVideos(key, form.videos[key].filter((_, position) => position !== index));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.general.appName.trim()) {
      Swal.fire("Validation Error", "App name is required", "error");
      return;
    }

    if (!form.orders.allowCod && !Object.values(form.orders.gateways).some(Boolean)) {
      Swal.fire("Validation Error", "Enable at least one payment method", "error");
      return;
    }

    for (const { key, label } of VIDEO_LISTS) {
      const invalidIndex = form.videos[key].findIndex(
        (video) => !video.title.trim() || !isValidVideoUrl(video.url),
      );
      if (invalidIndex !== -1) {
        Swal.fire(
          "Validation Error",
          `${label} #${invalidIndex + 1}: enter a title and a valid link (http/https)`,
          "error",
        );
        return;
      }
    }

    setSaving(true);
    try {
      const result = await apiRequest("/settings", "PUT", {
        general: {
          ...form.general,
          appName: form.general.appName.trim(),
        },
        features: form.features,
        orders: {
          defaultShippingCharge: Number(form.orders.defaultShippingCharge || 0),
          returnWindowDays: Number(form.orders.returnWindowDays || 0),
          allowCod: form.orders.allowCod,
          gateways: form.orders.gateways,
        },
        mlm: {
          withdrawAdminPercent: Number(form.mlm.withdrawAdminPercent || 0),
          tdsPercent: Number(form.mlm.tdsPercent || 0),
          minWithdrawAmount: Number(form.mlm.minWithdrawAmount || 0),
          profitWithdrawStartHour: Number(form.mlm.profitWithdrawStartHour || 10),
          profitWithdrawEndHour: Number(form.mlm.profitWithdrawEndHour || 18),
        },
        videos: {
          seminars: form.videos.seminars.map((video) => ({ title: video.title.trim(), url: video.url.trim() })),
          achievements: form.videos.achievements.map((video) => ({ title: video.title.trim(), url: video.url.trim() })),
        },
      });

      if (!result.success) {
        throw new Error(result.message || "Unable to save settings");
      }

      setForm(mapSettingsToForm(result.settings));
      Swal.fire({
        title: "Settings Saved",
        text: "App settings have been updated.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Save Failed", error.message || "Unable to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Settings…</h2>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Configuration</div>
          <h1 className="page-title">
            App <em>Settings</em>
          </h1>
          <div className="page-sub">
            Manage store details, checkout rules and app features.
            {" "}<Link to="/club-income" style={{ color: "var(--brand)", textDecoration: "underline" }}>Manage club income and rank payouts</Link>.
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>General</h2>
            <p>Brand and support details shown in the app</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">App Name</label>
              <input
                className="admin-input"
                value={form.general.appName}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, appName: event.target.value },
                  })
                }
                placeholder="Riseway"
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Tagline</label>
              <input
                className="admin-input"
                value={form.general.tagline}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, tagline: event.target.value },
                  })
                }
                placeholder="Style that rises with you"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Support Email</label>
              <input
                type="email"
                className="admin-input"
                value={form.general.supportEmail}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, supportEmail: event.target.value },
                  })
                }
                placeholder="support@riseway.com"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Support Phone</label>
              <input
                className="admin-input"
                value={form.general.supportPhone}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, supportPhone: event.target.value },
                  })
                }
                placeholder="9876543210"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">WhatsApp Number</label>
              <input
                className="admin-input"
                value={form.general.whatsappNumber}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, whatsappNumber: event.target.value },
                  })
                }
                placeholder="9876543210"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Store Address</label>
              <input
                className="admin-input"
                value={form.general.address}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, address: event.target.value },
                  })
                }
                placeholder="Jaipur, Rajasthan"
              />
            </div>
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>App Features</h2>
            <p>Turn key app capabilities on or off</p>
          </div>
          <div className="admin-card-body page-stack">
            <ToggleSwitch
              label="Maintenance Mode"
              hint="App can show a maintenance screen using this flag"
              checked={form.features.maintenanceMode}
              onChange={(checked) =>
                setForm({
                  ...form,
                  features: { ...form.features, maintenanceMode: checked },
                })
              }
            />
            <ToggleSwitch
              label="Allow New Registrations"
              hint="Disable to stop OTP register for new customers"
              checked={form.features.allowNewRegistrations}
              onChange={(checked) =>
                setForm({
                  ...form,
                  features: { ...form.features, allowNewRegistrations: checked },
                })
              }
            />
            <ToggleSwitch
              label="Allow Reviews"
              hint="Disable to block new and edited product reviews"
              checked={form.features.allowReviews}
              onChange={(checked) =>
                setForm({
                  ...form,
                  features: { ...form.features, allowReviews: checked },
                })
              }
            />
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>Orders & Payments</h2>
            <p>Shipping, returns and enabled payment methods</p>
          </div>
          <div className="admin-card-body page-stack">
            <div className="admin-form-grid">
              <div className="form-field">
                <label className="modal-label">Default Shipping Charge (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="admin-input"
                  value={form.orders.defaultShippingCharge}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      orders: { ...form.orders, defaultShippingCharge: event.target.value },
                    })
                  }
                />
                <small className="form-hint">Auto-fills in product add. Can be changed per product.</small>
              </div>
              <div className="form-field">
                <label className="modal-label">Return Window (Days)</label>
                <input
                  type="number"
                  min="0"
                  className="admin-input"
                  value={form.orders.returnWindowDays}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      orders: { ...form.orders, returnWindowDays: event.target.value },
                    })
                  }
                />
              </div>
            </div>
            <ToggleSwitch
              label="Cash on Delivery"
              checked={form.orders.allowCod}
              onChange={(checked) =>
                setForm({ ...form, orders: { ...form.orders, allowCod: checked } })
              }
            />
            {gateways.map((gateway) => (
              <ToggleSwitch
                key={gateway.name}
                label={gateway.label}
                hint="Online payment gateway"
                checked={form.orders.gateways[gateway.name] === true}
                onChange={(checked) =>
                  setForm({
                    ...form,
                    orders: {
                      ...form.orders,
                      gateways: { ...form.orders.gateways, [gateway.name]: checked },
                    },
                  })
                }
              />
            ))}
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>MLM Wallet</h2>
            <p>Admin charge on every withdraw. TDS only on working commission. Profit fund window is IST.</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Minimum withdraw amount (₹)</label>
              <input
                type="number"
                min="0"
                step="1"
                className="admin-input"
                value={form.mlm.minWithdrawAmount}
                onChange={(event) =>
                  setForm({
                    ...form,
                    mlm: { ...form.mlm, minWithdrawAmount: event.target.value },
                  })
                }
              />
              <small className="form-hint">User can withdraw only if amount is at least this value.</small>
            </div>
            <div className="form-field">
              <label className="modal-label">Withdraw admin charge (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="admin-input"
                value={form.mlm.withdrawAdminPercent}
                onChange={(event) =>
                  setForm({
                    ...form,
                    mlm: { ...form.mlm, withdrawAdminPercent: event.target.value },
                  })
                }
              />
            </div>
            <div className="form-field">
              <label className="modal-label">TDS on eWallet withdraw (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="admin-input"
                value={form.mlm.tdsPercent}
                onChange={(event) =>
                  setForm({
                    ...form,
                    mlm: { ...form.mlm, tdsPercent: event.target.value },
                  })
                }
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Profit withdraw start (hour, 0–23)</label>
              <input
                type="number"
                min="0"
                max="23"
                className="admin-input"
                value={form.mlm.profitWithdrawStartHour}
                onChange={(event) =>
                  setForm({
                    ...form,
                    mlm: { ...form.mlm, profitWithdrawStartHour: event.target.value },
                  })
                }
              />
              <small className="form-hint">Mon–Fri only. Default 10 = 10:00 am IST.</small>
            </div>
            <div className="form-field">
              <label className="modal-label">Profit withdraw end (hour, 1–24)</label>
              <input
                type="number"
                min="1"
                max="24"
                className="admin-input"
                value={form.mlm.profitWithdrawEndHour}
                onChange={(event) =>
                  setForm({
                    ...form,
                    mlm: { ...form.mlm, profitWithdrawEndHour: event.target.value },
                  })
                }
              />
              <small className="form-hint">Default 18 = 6:00 pm IST. End hour is exclusive.</small>
            </div>
          </div>
        </section>

        {VIDEO_LISTS.map(({ key, label, hint }) => (
          <section className="table-card" key={key}>
            <div className="table-head">
              <h2>{label}</h2>
              <p>{hint}. Paste a YouTube or any video link.</p>
            </div>
            <div className="admin-card-body page-stack">
              {form.videos[key].length === 0 && (
                <small className="form-hint">No videos added yet.</small>
              )}
              {form.videos[key].map((video, index) => (
                <div key={index} className="video-row">
                  <div className="form-field">
                    <label className="modal-label">Video Title</label>
                    <input
                      className="admin-input"
                      value={video.title}
                      maxLength={150}
                      onChange={(event) => changeVideo(key, index, "title", event.target.value)}
                      placeholder="Annual Seminar 2026"
                    />
                  </div>
                  <div className="form-field">
                    <label className="modal-label">Video Link</label>
                    <input
                      type="url"
                      className="admin-input"
                      value={video.url}
                      maxLength={500}
                      onChange={(event) => changeVideo(key, index, "url", event.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                  <button
                    type="button"
                    className="action-button delete video-row-delete"
                    title="Remove video"
                    aria-label="Remove video"
                    onClick={() => removeVideo(key, index)}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <div>
                <button type="button" className="btn-sm btn-outline" onClick={() => addVideo(key)}>
                  <Plus size={16} aria-hidden="true" /> Add Video
                </button>
              </div>
            </div>
          </section>
        ))}

        <div className="page-hdr-actions">
          <button type="submit" className="btn-sm btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
